import { useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { toast } from "sonner";
import { UserPlus, Shield, Loader2, Send, KeyRound } from "lucide-react";

export default function AdminInvite() {
  const [step, setStep] = useState(1);
  const [method, setMethod] = useState(null);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [targetEmail, setTargetEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const requestOtp = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/invite-admin/request-otp");
      setMethod(data.method);
      if (data.method === "email") setMaskedEmail(data.email);
      setStep(2);
      if (data.method === "totp") {
        toast.info("Entrez le code de votre application d'authentification");
      } else {
        toast.success(`Code envoyé à ${data.email}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!targetEmail.trim() || code.length !== 6) return;
    setLoading(true);
    try {
      const { data } = await api.post("/admin/invite-admin/send", {
        email: targetEmail.trim(),
        code,
      });
      toast.success(`Invitation envoyée à ${data.email}`);
      setTargetEmail("");
      setCode("");
      setStep(1);
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <AdminPageHeader
        title="Invitations admin"
        subtitle="Inviter un nouvel administrateur avec vérification 2FA"
      />

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">Sécurité renforcée</h2>
                <p className="text-xs text-slate-400">Double authentification requise pour chaque invitation</p>
              </div>
            </div>

            <ol className="space-y-4 text-sm text-slate-600">
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shrink-0">1</span>
                <span>Vérifiez votre identité via TOTP ou code email</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shrink-0">2</span>
                <span>Saisissez l&apos;email du futur administrateur</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shrink-0">3</span>
                <span>Un lien valide 48h lui est envoyé par email</span>
              </li>
            </ol>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            {step === 1 ? (
              <div className="text-center py-8">
                <UserPlus className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h2 className="font-semibold text-slate-900 mb-2">Nouvelle invitation</h2>
                <p className="text-sm text-slate-400 mb-6">
                  Commencez par vérifier votre identité administrateur
                </p>
                <Button onClick={requestOtp} disabled={loading} className="rounded-full gap-2">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                  Vérifier mon identité
                </Button>
              </div>
            ) : (
              <form onSubmit={sendInvite} className="space-y-4">
                <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-sm text-blue-700">
                  {method === "totp"
                    ? "Entrez le code de votre application d'authentification (6 chiffres)"
                    : `Code envoyé à ${maskedEmail}`}
                </div>

                <div>
                  <Label>Code de vérification (6 chiffres)</Label>
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="mt-1.5 h-11 text-center text-lg tracking-widest font-mono"
                    placeholder="000000"
                    maxLength={6}
                  />
                </div>

                <div>
                  <Label>Email du futur administrateur</Label>
                  <Input
                    type="email"
                    value={targetEmail}
                    onChange={(e) => setTargetEmail(e.target.value)}
                    className="mt-1.5 h-11"
                    placeholder="admin@example.com"
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="rounded-full">
                    Retour
                  </Button>
                  <Button type="submit" disabled={loading || !targetEmail || code.length !== 6} className="rounded-full gap-2 flex-1">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Envoyer l&apos;invitation
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}